/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * File system utility functions
 */
import { existsSync } from 'fs';
import { readFile, readdir } from 'fs/promises';
import { join } from 'path';
import fg from 'fast-glob';

/**
 * Check if a file exists
 */
export const fileExists = (path: string): boolean => {
  return existsSync(path);
};

/**
 * Read and parse JSON file
 */
export const readJsonFile = async <T = unknown>(path: string): Promise<T | null> => {
  try {
    const content = await readFile(path, 'utf-8');
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
};

/**
 * Check if any of the given files exist in the directory
 */
export const hasAnyFile = (cwd: string, files: string[]): boolean => {
  return files.some((file) => fileExists(join(cwd, file)));
};

/**
 * Find files matching a glob pattern
 */
export const findFiles = async (pattern: string | string[], cwd: string): Promise<string[]> => {
  return fg(pattern, {
    cwd,
    dot: true,
    onlyFiles: true,
  });
};

/**
 * Get all directories in a path
 */
export const getDirectories = async (path: string): Promise<string[]> => {
  try {
    const entries = await readdir(path, { withFileTypes: true });
    return entries.filter((entry) => entry.isDirectory()).map((entry) => entry.name);
  } catch {
    return [];
  }
};

/**
 * Read package.json from a directory
 */
export const readPackageJson = async (
  cwd: string
): Promise<Record<string, unknown> | null> => {
  return readJsonFile(join(cwd, 'package.json'));
};

/**
 * Check if a package.json has a specific dependency
 */
export const hasDependency = async (cwd: string, depName: string): Promise<boolean> => {
  const pkg = await readPackageJson(cwd);
  if (!pkg) return false;

  const deps = pkg.dependencies as Record<string, string> | undefined;
  const devDeps = pkg.devDependencies as Record<string, string> | undefined;

  return Boolean(deps?.[depName] || devDeps?.[depName]);
};

/**
 * Check if multiple dependencies exist
 */
export const hasAnyDependency = async (cwd: string, depNames: string[]): Promise<boolean> => {
  const pkg = await readPackageJson(cwd);
  if (!pkg) return false;

  const deps = pkg.dependencies as Record<string, string> | undefined;
  const devDeps = pkg.devDependencies as Record<string, string> | undefined;
  const allDeps = { ...deps, ...devDeps };

  return depNames.some((dep) => Boolean(allDeps[dep]));
};

